import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Users, FolderKanban, FileText, FileSignature, X, Loader2 } from "lucide-react";

/**
 * <GlobalSearch /> — Cmd+K palette extension that searches across
 * clients, projects, invoices and estimates. Replaces the previous
 * route-only command palette behavior with real data lookups.
 */
const ICONS = { clients: Users, projects: FolderKanban, invoices: FileText, estimates: FileSignature };

export default function GlobalSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ clients: [], projects: [], invoices: [], estimates: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setData({ clients: [], projects: [], invoices: [], estimates: [] }); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) { setData({ clients: [], projects: [], invoices: [], estimates: [] }); return; }
    const ctrl = new AbortController();
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/search", { params: { q }, signal: ctrl.signal })
        .then((r) => setData(r.data))
        .catch((err) => { if (err.name !== "CanceledError") console.warn("search:", err); })
        .finally(() => setLoading(false));
    }, 200);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, open]);

  if (!open) return null;

  const go = (path) => { onClose(); navigate(path); };
  const total = data.clients.length + data.projects.length + data.invoices.length + data.estimates.length;

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm pt-[10vh]" onClick={onClose} role="dialog" aria-modal="true" data-testid="global-search">
      <div className="max-w-xl mx-auto bg-white dark:bg-slate-900 rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E2E8F0] dark:border-slate-800">
          {loading ? <Loader2 className="w-4 h-4 text-[#C9A356] animate-spin" /> : <Search className="w-4 h-4 text-[#94A3B8]" />}
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search clients, projects, invoices, estimates…"
                 data-testid="global-search-input"
                 className="flex-1 bg-transparent outline-none text-sm" />
          <button onClick={onClose} aria-label="Close" className="text-[#94A3B8] hover:text-[#0A2540]"><X className="w-4 h-4" /></button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {q.trim().length < 2 ? (
            <p className="px-4 py-8 text-sm text-center text-[#94A3B8]">Type at least 2 characters to search.</p>
          ) : total === 0 && !loading ? (
            <p className="px-4 py-8 text-sm text-center text-[#94A3B8]" data-testid="global-search-empty">No matches for "{q}"</p>
          ) : (
            <>
              {Object.entries(data).map(([group, items]) => items.length > 0 && (
                <Group key={group} kind={group} items={items} onPick={go} />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Group({ kind, items, onPick }) {
  const Icon = ICONS[kind];
  const labels = { clients: "Clients", projects: "Projects", invoices: "Invoices", estimates: "Estimates" };

  const pathFor = (i) => {
    if (kind === "clients") return `/admin/clients/${i.user_id}`;
    if (kind === "projects") return `/admin/projects/${i.project_id}`;
    if (kind === "invoices") return `/admin/payments?focus=${i.invoice_id || i.invoice_number}`;
    if (kind === "estimates") return `/admin/estimates?focus=${i.estimate_id || i.estimate_number}`;
    return "/";
  };
  const titleFor = (i) => {
    if (kind === "clients") return i.name || i.email;
    if (kind === "projects") return i.title || i.project_id;
    if (kind === "invoices") return `Invoice ${i.invoice_number || i.invoice_id}`;
    if (kind === "estimates") return i.title || `Estimate ${i.estimate_number || i.estimate_id}`;
    return "—";
  };
  const subFor = (i) => {
    if (kind === "clients") return i.customer_code || i.email;
    if (kind === "projects") return `${i.customer_code || ""} · ${i.status || ""}`.trim();
    if (kind === "invoices") return `$${(i.amount || 0).toFixed(2)} · ${i.status}`;
    if (kind === "estimates") return `$${(i.total || 0).toFixed(2)} · ${i.status}`;
    return "";
  };

  return (
    <div>
      <div className="px-4 py-2 bg-slate-50 dark:bg-slate-900 text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> {labels[kind]} ({items.length})
      </div>
      <ul>
        {items.map((it, idx) => (
          <li key={idx}>
            <button
              onClick={() => onPick(pathFor(it))}
              data-testid={`gs-${kind}-${idx}`}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 border-b border-[#E2E8F0] dark:border-slate-800 last:border-0 transition"
            >
              <div className="text-sm font-medium text-[#0A2540] dark:text-white truncate">{titleFor(it)}</div>
              <div className="text-xs text-[#475569] dark:text-slate-400 truncate">{subFor(it)}</div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
